package com.evoapps.evoshift.salesimport

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.salesimport.SalesSheet
import com.evoapps.evoshift.spreadsheet.spreadsheetDropSpec
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.datetime.YearMonth
import kotlinx.datetime.yearMonth

/**
 * SH-161 F4 (Play's Tier 1 File_Handlers): a sales file the system opened the app with ("Open
 * with" in Files, "Share" from the till's app or a mail): its name and bytes, null when it could
 * not be read (the import then says so, as for a picked file).
 */
class IncomingSalesFile(val name: String, val bytes: ByteArray?)

/** Where the person sent it: a workplace and, from a file of several months, one of them (null: all). */
class SalesFileHandoff(val file: IncomingSalesFile, val workplaceId: String, val month: YearMonth?)

/**
 * The way in for such files: [offer]ed by the platform (Android's MainActivity), it [waiting]
 * until the person is signed in and picks the workplace and month (App.kt's dialog), then
 * [handOver]ed to the Sales import of that workplace, which [take]s it once (SalesImportViewModel).
 */
open class SalesFileInbox {
    private val _waiting = MutableStateFlow<IncomingSalesFile?>(null)
    val waiting: StateFlow<IncomingSalesFile?> = _waiting.asStateFlow()
    private val _handoff = MutableStateFlow<SalesFileHandoff?>(null)
    val handoff: StateFlow<SalesFileHandoff?> = _handoff.asStateFlow()

    /** A file opened with the app; a newer one replaces one still waiting. */
    fun offer(file: IncomingSalesFile) {
        _waiting.value = file
    }

    /** The person closed the question: the file is dropped. */
    fun dismiss() {
        _waiting.value = null
    }

    /** The waiting file goes to [workplaceId]'s import, only [month]'s days when given. */
    fun handOver(workplaceId: String, month: YearMonth?) {
        val file = _waiting.value ?: return
        _waiting.value = null
        _handoff.value = SalesFileHandoff(file, workplaceId, month)
    }

    /** The file handed to [workplaceId], once; null when there is none (or it is another workplace's). */
    fun take(workplaceId: String): SalesFileHandoff? =
        _handoff.value?.takeIf { it.workplaceId == workplaceId }?.also { _handoff.value = null }

    /** A handed-over file nobody can import (the workplace's import is not open to the person). */
    fun clearHandoff() {
        _handoff.value = null
    }

    companion object {
        /** Whether a file of [name] (and [mimeType], when known) is a sales export the import reads. */
        fun accepts(name: String, mimeType: String?): Boolean = spreadsheetDropSpec.accepts(name, mimeType)
    }
}

/** The app's inbox (one per process: the platform hands files to the main window). */
object IncomingSalesFiles : SalesFileInbox()

/** The months the sheet's days fall in, the earliest first. */
fun SalesSheet.months(): List<YearMonth> = days.map { it.date.yearMonth }.distinct().sorted()

/** Only [month]'s days (null: the whole sheet); the rows left out stay listed. */
fun SalesSheet.onlyMonth(month: YearMonth?): SalesSheet =
    if (month == null) this else copy(days = days.filter { it.date.yearMonth == month })

/**
 * The workplaces among [workplaces] (id to name, in the switcher's order) whose takings [user] may
 * import: those where they see the finances (owners and managers with access to finances).
 */
fun salesImportWorkplaces(user: User?, workplaces: Map<String, String>): List<Pair<String, String>> =
    workplaces.filterKeys { Capability.VIEW_FINANCES in user?.capabilitiesAt(it).orEmpty() }.toList()
